'use client';

import { KeyVisualLegacy } from '@pixum/key-visual/lib/variants/legacy/KeyVisualLegacy';
import { TEXT_VARIANT } from '@pixum/text';
import { Image, IMAGE_LOADING, ImageFetchPriority } from '../../image';
import { Voucher } from '../../voucher';
import { KeyVisualActions, KeyVisualPicture } from '../partials';
import type { KeyVisualClassicProps } from '../types';
import { scrollToElementIntoView, navigateTo } from 'utils';
import { REVIEW_SECTION_ID } from '../../../constants';
import { Video } from '../../video/Video';

/**
 * KeyVisualClassic component - Classic variant using KeyVisualLegacy.
 *
 * Renders the classic key visual layout with column layout on mobile
 * and row layout on desktop.
 *
 * @param props - KeyVisual variant component properties
 * @returns Rendered classic key visual variant
 */
export function KeyVisualClassic(props: KeyVisualClassicProps) {
  const {
    keyVisual: {
      image,
      links = [],
      id,
      title,
      subtitle,
      pictureData,
      imageVariantsByDeviceSize,
      constants,
      video,
      isClickable
    },
    device,
    voucher,
    hasHotspots,
    featuredAward,
    text,
    footNotes,
    countdown
  } = props;

  const hasVideo = video && !!video?.src;
  const ratingDescription = props?.customerReviews ? `(${props.customerReviews.ratingDescription})` : '';
  const shouldShowCustomerRatings = props?.customerReviews && props.customerReviews.count > 0;
  const customerRatings = shouldShowCustomerRatings ? props?.customerReviews?.rating : 0;
  const link = links[0];

  const handleRatingClick = () => scrollToElementIntoView(REVIEW_SECTION_ID);

  /**
   * Handles video click by navigating to the link URL.
   */
  const handleVideoClick = () => {
    if (!link) return;

    navigateTo(link.url);
  };

  /**
   * ActionsWithFootnotes - Wrapper component for action buttons and footnotes.
   * Only wraps in div if footnotes exist, otherwise just returns actions.
   */
  const ActionsWithFootnotes = () => {
    if (!footNotes) {
      // No wrapper needed if no footnotes
      return <KeyVisualActions buttons={links} device={device} constants={constants} />;
    }

    return (
      <div className="key-visual__actions-wrapper">
        <KeyVisualActions buttons={links} device={device} constants={constants} />
        {footNotes}
      </div>
    );
  };

  return (
    <KeyVisualLegacy
      actions={<ActionsWithFootnotes />}
      awardImage={
        featuredAward ? (
          <Image
            device={device}
            loading={IMAGE_LOADING.EAGER}
            fetchPriority={ImageFetchPriority.High}
            image={featuredAward.image}
          />
        ) : undefined
      }
      headlineText={title}
      headlineVariant={TEXT_VARIANT.TITLE_1}
      dataLayout="row"
      isClickable={isClickable}
      media={
        hasVideo ? (
          <div className="key-visual--video" aria-label={link?.title} onClick={handleVideoClick}>
            <Video video={{ ...video, autoPlay: true }} loadInstantly renderSchema={false} />
          </div>
        ) : (
          <KeyVisualPicture
            image={image}
            pictureData={pictureData}
            imageVariantsByDeviceSize={imageVariantsByDeviceSize}
            device={device}
            hasHotSpots={hasHotspots}
          />
        )
      }
      rating={customerRatings}
      ratingDescription={ratingDescription}
      ratingClick={props.customerReviews ? handleRatingClick : undefined}
      subHeadlineText={subtitle}
      supportingText={text}
      id={id}
      withScale={!hasHotspots}
      voucher={
        voucher && (
          <Voucher sources={['props', 'url', 'localStorage']} variant="extended" addToCart={true} {...voucher} />
        )
      }
      countdown={countdown}
    />
  );
}
